import { AccountPermissions } from '@ajs/models/account/AccountDataModel';
import { FtpLiabilityAcceptanceService } from '@app/core/services/ftp-liability-acceptance.service';
import { DbModel } from '@app/databases/models/db.model';
import { PrivacyLevel } from '@app/user/enums/privacy-level.enum';
import { UserDataModel } from '@app/user/models/user-data.model';
import { BehaviorSubject, catchError, distinctUntilChanged, filter, Observable, of, switchMap, tap } from 'rxjs';
import { AccountDataModel, AccountModel, InvitedPermissionsStatus } from '../models/account';
import { UserModel } from '../models/user';
import DatabasesDataService from './DatabasesDataService';
import ImpersonateUserService from './ImpersonateUserService';
import LogService from './LogService';
import UsersDataService from './UsersDataService';

export default class AppStateService {

    static $inject = [
        '$cookies',
        '$q',
        '$timeout',
        '$window',
        'DatabasesDataService',
        'LogService',
        'UsersDataService',
        'ImpersonateUserService',
        'fdxLiabilityAcceptanceService'
    ];

    // Local Attributes
    account = null;
    activeTab = '';
    database: DbModel | null = null;
    pageTitle = '';
    user: UserModel = null;
    insecureFTPAllowed: boolean = false;

    // Todo: this will be updated in the future
    private readonly _userLoaded$: BehaviorSubject<UserModel | null> = new BehaviorSubject<UserModel | null>(null);
    userLoaded$: Observable<UserModel | null> = this._userLoaded$.asObservable();

    private readonly _databaseLoaded$: BehaviorSubject<DbModel | null> = new BehaviorSubject<DbModel | null>(null);
    databaseLoaded$: Observable<DbModel | null> = this._databaseLoaded$.asObservable();

    private readonly _accountLoaded$: BehaviorSubject<AccountDataModel | null> = new BehaviorSubject<AccountDataModel | null>(null);
    accountLoaded$: Observable<AccountDataModel | null> = this._accountLoaded$.asObservable();

    constructor(
        private readonly $cookies,
        private readonly $q,
        private readonly $timeout,
        private readonly $window: Window,
        private readonly databasesDataService: DatabasesDataService,
        private readonly logService: LogService,
        private readonly usersDataService: UsersDataService,
        private readonly impersonateUserService: ImpersonateUserService,
        private readonly liabilityAcceptanceService: FtpLiabilityAcceptanceService
    ) {
        this.updateFtpLiabilityOnDatabaseChange();
    }

    getAccountId() {
        return this.account?.id;
    }

    setAccount(value: AccountDataModel | null): void {
        if (!value) {
            this.account = null;
        } else {
            this.account = new AccountModel(value);
        }

        this._accountLoaded$.next(value);
    }

    getAccount(): AccountModel | null {
        return this.account;
    }

    saveAccountId(accountId) {

        const expirationDate = new Date();
        expirationDate.setTime(expirationDate.getTime() + 30 * (60 * 60 * 24) * 1000);

        this.$cookies.put('account_id', accountId, { path: '/', expires: expirationDate.toUTCString() })

    }

    getSavedAccountId() {
        return this.$cookies.get('account_id');
    }

    setDatabase(value: DbModel): void {
        this.database = value;
        this._databaseLoaded$.next(value);
    }

    getDatabase(): DbModel | null {
        return this.database;
    }

    getDatabaseId(): string {
        return this.database?.id;
    }

    getDatabaseName(): string {
        return this.database?.name;
    }

    setUser(value: UserDataModel): void {
        this.user = new UserModel(value);
        this._userLoaded$.next(this.user);
    }

    reloadUser() {
        const def = this.$q.defer();

        const promise = this.usersDataService.retrieveCurrentUser();

        promise.then(
            (user) => {
                this.setUser(user);
                def.resolve(user);
            },
            () => {
                this.setUser(null);
                def.reject(null);
            }
        );

        return def.promise;
    }

    getUser(): UserModel {
        return this.impersonateUserService.getUser() || this.user;
    }

    getLoggedInUser(): UserModel {
        return this.user;
    }

    getUserId(): string {
        return this.getUser().user_id;
    }

    getImpersonatedUserId() {
        return this.$cookies.get('impersonated_user_id') ?? null;
    }

    updateUserSetting(key: string, value: string | boolean): Promise<any> {
        return this.usersDataService.updateUserSetting(
            this.user.settings,
            key,
            value
        );
    }

    isPrivacyLevelAtLeastAnalyst(restrictDemoMode: boolean = true): boolean {
        return this.userHasMinPrivacyLevel(PrivacyLevel.Analyst, restrictDemoMode);
    }

    isPrivacyLevelAtLeastAdmin(): boolean {
        return this.userHasMinPrivacyLevel(PrivacyLevel.Admin);
    }

    private userHasMinPrivacyLevel(minPrivacyLevel: PrivacyLevel, restrictDemoMode: boolean = true): boolean {
        return this.user?.hasMinimumPrivacyLevel(minPrivacyLevel, this.getAccountId(), restrictDemoMode);
    }

    isDbTemplateAccount(): boolean {
        return this.account.features.rakuten_rad_feed_build_template === 'enabled';
    }

    isAllowReadOnlyAccount(): boolean {
        return this.account.features.rakuten_read_only_permission === 'enabled';
    }

    getAccountPermissions() {
        if (this.account?.permissions) {
            return this.account.permissions;
        }

        return null;

    }

    isInsecureFTPAllowed(): boolean {
        return this.insecureFTPAllowed;
    }

    isPrimaryUser(): boolean {
        return this.account?.permissions === 'primary';
    }

    isInvitedPrimaryUser(): boolean {
        return this.account?.invited_permissions?.status === InvitedPermissionsStatus.ACCEPTED &&
        this.account.invited_permissions.permissions === AccountPermissions.primary;
    }

    loadDatabase(databaseId) {
        const deferred = this.$q.defer();

        this.databasesDataService.getOneById(databaseId).then(
            (db) => {
                this.setDatabase(db);
                deferred.resolve(db);
            },
            (error) => {
                this.logService.error('AppStateService', 'loadDatabase', error);
                deferred.reject(error);
            }
        );

        return deferred.promise;
    }

    private updateFtpLiabilityOnDatabaseChange(): void {
        this.insecureFTPAllowed = false;

        this.databaseLoaded$.pipe(
            filter((db) => Boolean(db?.id)),
            distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
            switchMap((db) => {
                return this.liabilityAcceptanceService.hasAcceptedFTPLiability(db.id)
            }),
            tap((hasAccepted) => { this.insecureFTPAllowed = hasAccepted }),
            catchError((error) => {
                this.logService.error('AppStateService', 'hasAcceptedFTPLiability', error);
                return of(null);
            })
        ).subscribe();
    }
}

export { AppStateService };
